import { createTheme } from '@mui/material/styles'

// extend palette with rainbow colors
declare module '@mui/material/styles' {
  interface Palette {
    rainbow: {
      red: string
      yellow: string
      blue: string
      purple: string
      green: string
      teal: string
    }
  }
  interface PaletteOptions {
    rainbow: {
      red: string
      yellow: string
      blue: string
      purple: string
      green: string
      teal: string
    }
  }

  interface TypeText {
    light: string
    red: string
  }
}

// extend component variants
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    rainbow: true
    link: true
    destroy: true
  }
}

// extend IconButton colors
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    red: true
    yellow: true
    blue: true
    purple: true
    green: true
    teal: true
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    red: true
    yellow: true
    blue: true
    purple: true
    green: true
    teal: true
  }
}

declare module '@mui/material/Divider' {
  interface DividerPropsVariantOverrides {
    rainbow: true
  }
}

// extend background with rainbow
declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    dialog: string
    rainbow: string
    rainbowDisabled: string
    mid: string
  }
}

export const rainbowColors = {
  enabled: ['rgb(255, 231, 1)', 'rgb(250, 81, 211)', 'rgb(16, 217, 237)', 'rgb(82, 255, 83)'],
  disabled: [
    'rgba(255, 231, 1, 0.2)',
    'rgba(250, 81, 211, 0.2)',
    'rgba(16, 217, 237, 0.2)',
    'rgba(82, 255, 83, 0.2)',
  ],
}

export const theme = createTheme({
  palette: {
    primary: {
      main: '#ffffff',
      contrastText: '#15161C',
    },
    secondary: {
      main: '#191919',
      light: '#2c2c2c',
      contrastText: '#ffffff',
    },
    background: {
      default: '#15161C',
      paper: '#1E1F27',
      dialog: '#000000',
      mid: '#212227',
      rainbow: `linear-gradient(to right, ${rainbowColors.enabled[0]}, ${rainbowColors.enabled[1]}, ${rainbowColors.enabled[2]}, ${rainbowColors.enabled[3]})`,
      rainbowDisabled: `linear-gradient(to right, ${rainbowColors.disabled[0]}, ${rainbowColors.disabled[1]}, ${rainbowColors.disabled[2]}, ${rainbowColors.disabled[3]})`,
    },
    divider: '#7A7E8B',
    text: {
      primary: '#ffffff',
      secondary: '#A3A3A3',
      light: '#c1c1c1',
      red: '#e04949',
    },
    success: {
      main: '#4AE97A',
    },
    warning: {
      main: '#2F539D',
    },
    error: {
      main: '#FF6257',
    },
    rainbow: {
      red: '#F850D3',
      yellow: '#FFE700',
      blue: '#36C3E9',
      purple: '#CF6ED3',
      green: '#4CD166',
      teal: '#36DAED',
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    allVariants: {
      fontFamily: 'SF Rounded',
      userSelect: 'none',
      WebkitUserSelect: 'none',
    },
    h1: {
      fontWeight: 800,
      fontSize: 100,
      lineHeight: '100px',
    },
    h2: {
      fontWeight: 700,
      fontSize: 34,
    },
    h3: {
      fontWeight: 500,
      fontSize: 17,
    },
    body1: {
      fontWeight: 500,
      fontSize: 16,
      lineHeight: '20px',
    },
    body2: {
      fontWeight: 400,
      fontSize: 15,
      lineHeight: '20px',
    },
    caption: {
      fontSize: 13,
      fontWeight: 400,
      color: 'rgb(163, 163, 163)',
    },
  },
  components: {
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#000000',
          elevation: '12',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: '#16171C',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1F27',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontWeight: 500,
        },
        secondary: {
          fontWeight: 500,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            borderRadius: 10,
            backgroundColor: theme.palette.background.paper,
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: { size: 'medium' },
      styleOverrides: {
        root: {
          '&:disabled': {
            color: '#ffffff',
          },
          fontWeight: 'bold',
          borderRadius: 20,
        },
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            minHeight: 32,
          },
        },
        {
          props: { size: 'medium' },
          style: {
            minHeight: 42,
          },
        },
        {
          props: { size: 'large' },
          style: {
            minHeight: 56,
          },
        },
        {
          props: { variant: 'rainbow' },
          style: {
            background: `linear-gradient(to right, ${rainbowColors.enabled[0]}, ${rainbowColors.enabled[1]}, ${rainbowColors.enabled[2]}, ${rainbowColors.enabled[3]})`,
            color: '#ffffff',
          },
        },
        {
          props: { variant: 'rainbow', disabled: true },
          style: {
            background: `linear-gradient(to right, ${rainbowColors.disabled[0]}, ${rainbowColors.disabled[1]}, ${rainbowColors.disabled[2]}, ${rainbowColors.disabled[3]})`,
            color: '#ffffff',
          },
        },
        {
          props: { variant: 'contained' },
          style: () => ({
            backgroundColor: '#33343A',
            color: '#ffffff',
          }),
        },
        {
          props: { variant: 'link' },
          style: ({ theme }) => ({
            color: theme.palette.rainbow.blue,
          }),
        },
        {
          props: { variant: 'destroy' },
          style: ({ theme }) => ({
            backgroundColor: '#FF6257',
            color: theme.palette.common.white,
          }),
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            opacity: 0.8,
            backgroundColor: theme.palette.secondary.main,
          },
        }),
      },
      variants: [
        {
          props: { color: 'red' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.rainbow.red,
            '& svg': {
              fill: theme.palette.secondary.main,
            },
            '&:hover': {
              opacity: 0.7,
              backgroundColor: theme.palette.rainbow.red,
            },
          }),
        },
        {
          props: { color: 'yellow' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.rainbow.yellow,
            '& svg': {
              fill: theme.palette.secondary.main,
            },
            '&:hover': {
              opacity: 0.7,
              backgroundColor: theme.palette.rainbow.yellow,
            },
          }),
        },
        {
          props: { color: 'blue' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.rainbow.blue,
            '& svg': {
              fill: theme.palette.secondary.main,
            },
            '&:hover': {
              opacity: 0.7,
              backgroundColor: theme.palette.rainbow.blue,
            },
          }),
        },
        {
          props: { color: 'purple' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.rainbow.purple,
            '& svg': {
              fill: theme.palette.secondary.main,
            },
            '&:hover': {
              opacity: 0.7,
              backgroundColor: theme.palette.rainbow.purple,
            },
          }),
        },
        {
          props: { color: 'green' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.rainbow.green,
            '& svg': {
              fill: theme.palette.secondary.main,
            },
            '&:hover': {
              opacity: 0.7,
              backgroundColor: theme.palette.rainbow.green,
            },
          }),
        },
        {
          props: { color: 'teal' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.rainbow.teal,
            '& svg': {
              fill: theme.palette.secondary.main,
            },
            '&:hover': {
              opacity: 0.7,
              backgroundColor: theme.palette.rainbow.teal,
            },
          }),
        },
      ],
    },
    MuiDivider: {
      variants: [
        {
          props: { variant: 'rainbow' },
          style: {
            border: 'none',
            height: 1,
            background: `linear-gradient(to right, ${rainbowColors.enabled[0]}, ${rainbowColors.enabled[1]}, ${rainbowColors.enabled[2]}, ${rainbowColors.enabled[3]})`,
          },
        },
      ],
    },
    MuiStack: {
      styleOverrides: {
        root: {
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
      },
    },
  },
})
